import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Controller, useForm } from 'react-hook-form';

import validationUtils from '../../services/validationUtils';
import TextInput from '../form/TextInput';
import PasswordConstraintChecker from '../form/PasswordConstraintChecker';

const PREFIX = 'ResetPasswordForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  password: `${PREFIX}-password`,
  error: `${PREFIX}-error`,
  submit: `${PREFIX}-submit`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: 305,
    padding: 32,
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.subtitle}`]: {
    fontSize: 18,
    fontWeight: 400,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.password}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.error}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.submit}`]: {
    marginTop: theme.spacing(4),
  },
}));

interface ResetPasswordFormProps {
  activate: boolean;
  email: string | null;
  error?: string;
  submit: (info: any) => void;
}

export default function ResetPasswordForm({
  activate,
  email,
  error,
  submit,
}: ResetPasswordFormProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');

  const { control, formState, handleSubmit, watch, getValues, setValue } =
    useForm({
      defaultValues: {
        Email: '',
        Code: '',
        Password: '',
      } as any,
    });
  const currentPassword = watch('Password');
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  React.useEffect(() => {
    if (email) {
      setValue('Email', email);
    }
  }, [email]);

  return (
    <Root className={classes.container}>
      <Typography className={classes.title}>
        {activate ? t('auth.activate') : t('auth.resetPassword')}
      </Typography>
      <form onSubmit={handleSubmit(submit)} className={classes.form}>
        {activate && (
          <Typography className={classes.subtitle}>
            {t('auth.newPasswordRequired')}
          </Typography>
        )}
        {!activate && (
          <TextInput
            label={t('auth.username')}
            name={'Email'}
            required={true}
            type="email"
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
              pattern: validationUtils.emailValidation(t),
            }}
          />
        )}
        {!activate && (
          <TextInput
            label={t('auth.resetCode')}
            className={classes.password}
            name={'Code'}
            required={true}
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
            }}
          />
        )}
        <FormControl variant="outlined" className={classes.password}>
          <InputLabel htmlFor="password" required={true}>
            {t('auth.password')}
          </InputLabel>
          <Controller
            name={'Password'}
            control={control}
            defaultValue={''}
            rules={{
              required: requiredFieldMessage,
              validate: validationUtils.validatePassword(t),
            }}
            render={({ field }) => (
              <OutlinedInput
                {...field}
                id="password"
                type={passwordVisible ? 'text' : 'password'}
                required={true}
                error={
                  formState.errors ? !!formState.errors['Password'] : false
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      onMouseDown={(evt) => evt.preventDefault()}
                      edge="end">
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t('auth.password')}
              />
            )}
          />
        </FormControl>
        <PasswordConstraintChecker currentValue={currentPassword} />
        <TextInput
          className={classes.password}
          label={t('auth.confirmPassword')}
          name={'ConfirmPassword'}
          required={true}
          control={control}
          errors={formState.errors}
          rules={{
            required: requiredFieldMessage,
            validate: validationUtils.validatePasswordMatch(getValues, t),
          }}
          type="password"
        />
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          className={classes.submit}
          type="submit">
          {activate ? t('auth.activate') : t('auth.resetPassword')}
        </Button>
      </form>
    </Root>
  );
}
